import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Command,
  Download,
  FlaskConical,
  Gauge,
  Inbox,
  LayoutDashboard,
  ListFilter,
  LoaderCircle,
  Mail,
  Menu,
  MessageSquareText,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
  XCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { ApiError, createCampaign, decideApproval, getCampaignWorkspace, getCockpit } from './api';
import { previewData } from './preview-data';
import type {
  Approval,
  Campaign,
  CockpitSnapshot,
  FunnelBucket,
  FunnelReport,
  InboxReply,
  Rate,
  StatusTone,
} from './types';

const navItems: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/today', label: 'Today', icon: LayoutDashboard },
  { to: '/campaigns', label: 'Campaigns', icon: Target },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/experiments', label: 'Experiments', icon: FlaskConical },
  { to: '/leads', label: 'Leads', icon: Users },
  { to: '/deliverability', label: 'Deliverability', icon: ShieldCheck },
  { to: '/insights', label: 'Insights', icon: BarChart3 },
  { to: '/playbooks', label: 'Playbooks', icon: BookOpen },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const phaseLabels: Record<string, string> = {
  intake: 'Intake',
  research: 'Research & ICP',
  offer: 'Offer Lab',
  list: 'List Lab',
  copy: 'Copy Lab',
  preflight: 'Preflight',
  experiment: 'Weekly Ritual',
  reply_ops: 'Reply Operations',
};

export default function App() {
  const location = useLocation();
  const hostedDemo = import.meta.env.VITE_DEMO_MODE === 'true';
  const preview = hostedDemo || (import.meta.env.DEV && new URLSearchParams(location.search).get('preview') === '1');
  const cockpit = useQuery({
    queryKey: ['cockpit', preview],
    queryFn: () => (preview ? Promise.resolve(previewData) : getCockpit()),
    refetchInterval: preview ? false : 60_000,
    retry: (count, error) => count < 2 && (!(error instanceof ApiError) || error.status >= 500),
  });

  if (cockpit.isPending) return <LoadingScreen />;
  if (cockpit.isError) return <ConnectionScreen error={cockpit.error} />;

  return <Shell data={cockpit.data} preview={preview} />;
}

function Shell({ data, preview }: { data: CockpitSnapshot; preview: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const location = useLocation();
  const current = navItems.find((item) => location.pathname.startsWith(item.to));

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <aside className={`sidebar ${menuOpen ? 'sidebar-open' : ''}`} aria-label="Primary navigation">
        <div className="brand-row">
          <Link to="/today" className="brand" aria-label="Outbound Brain home">
            <span className="brand-mark"><Zap size={17} aria-hidden="true" /></span>
            <span>Outbound Brain</span>
          </Link>
          <button className="icon-button mobile-only" onClick={() => setMenuOpen(false)} aria-label="Close navigation">
            <X size={20} />
          </button>
        </div>

        <button className="workspace-switcher" type="button">
          <span className="workspace-avatar">E</span>
          <span><strong>{data.session.workspace.name}</strong><small>Internal workspace</small></span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>

        <nav className="nav-list">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              <Icon size={18} aria-hidden="true" />
              <span>{label}</span>
              {label === 'Inbox' && data.inbox.length > 0 ? <span className="nav-count">{data.inbox.length}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <div className="system-mini">
            <span className={`health-dot ${data.today.data_quality.freshness === 'current' ? 'healthy' : 'warning'}`} />
            <span><strong>System health</strong><small>{humanize(data.today.data_quality.freshness)}</small></span>
            <ChevronRight size={15} aria-hidden="true" />
          </div>
          <div className="user-mini">
            <span className="user-avatar">{initials(data.session.user.name)}</span>
            <span><strong>{data.session.user.name}</strong><small>{humanize(data.session.user.role)}</small></span>
            <MoreHorizontal size={17} aria-hidden="true" />
          </div>
        </div>
      </aside>

      <div className="app-main">
        {preview ? <div className="preview-banner" role="status"><Sparkles size={15} /> Preview data — no actions affect production.</div> : null}
        <header className="topbar">
          <div className="topbar-left">
            <button className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu size={20} /></button>
            <span className="breadcrumb">{current?.label ?? 'Outbound Brain'}</span>
          </div>
          <div className="topbar-actions">
            <button className="ask-button" onClick={() => setCommandOpen(true)} type="button">
              <Sparkles size={16} aria-hidden="true" />
              <span>Ask Outbound Brain</span>
              <kbd>⌘ K</kbd>
            </button>
            <button className="icon-button" aria-label={`${data.notifications.length} unread notifications`}>
              <Bell size={19} />
              {data.notifications.length ? <span className="notification-dot" /> : null}
            </button>
          </div>
        </header>

        <main id="main-content" className="content" tabIndex={-1}>
          <Routes>
            <Route path="/today" element={<TodayPage data={data} preview={preview} />} />
            <Route path="/campaigns" element={<CampaignsPage data={data} preview={preview} />} />
            <Route path="/campaigns/:campaignId" element={<CampaignWorkspacePage data={data} preview={preview} />} />
            <Route path="/inbox" element={<InboxPage data={data} />} />
            <Route path="/experiments" element={<ExperimentsPage data={data} />} />
            <Route path="/leads" element={<LeadsPage data={data} />} />
            <Route path="/deliverability" element={<DeliverabilityPage data={data} />} />
            <Route path="/insights" element={<InsightsPage data={data} />} />
            <Route path="/playbooks" element={<PlaybooksPage data={data} />} />
            <Route path="/settings" element={<SettingsPage data={data} />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Routes>
        </main>
      </div>
      {menuOpen ? <button className="sidebar-scrim" onClick={() => setMenuOpen(false)} aria-label="Close navigation overlay" /> : null}
      {commandOpen ? <CommandPalette data={data} onClose={() => setCommandOpen(false)} /> : null}
    </div>
  );
}

function TodayPage({ data, preview }: { data: CockpitSnapshot; preview: boolean }) {
  const pending = data.approvals.filter((approval) => approval.status === 'pending');
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow={formatDate(new Date())}
        title={`Good morning, ${firstName(data.session.user.name)}.`}
        description="Here’s what needs your attention before anything else moves."
        action={<Link className="primary-button" to="/campaigns"><Plus size={17} /> New campaign</Link>}
      />

      <section className="focus-grid" aria-label="Current operating focus">
        <div className="focus-card bottleneck-card">
          <div className="card-kicker"><Gauge size={16} /> Bottleneck finder <Pill tone="info">{data.today.bottleneck.confidence} confidence</Pill></div>
          <h2>{data.today.bottleneck.title}</h2>
          <p>{data.today.bottleneck.detail}</p>
          <div className="next-action"><ArrowRight size={17} /><span><strong>Next best action</strong>{data.today.bottleneck.next_action}</span></div>
        </div>
        <div className="focus-card meeting-card">
          <div className="card-kicker"><Target size={16} /> Primary outcome</div>
          <div className="hero-number">{displayNumber(data.today.headline.qualified_meetings_held, data.today.headline.emails_sent > 0)}</div>
          <h2>Qualified meetings held</h2>
          <p>{data.today.headline.meetings_booked} booked · {formatRate(data.today.headline.held_rate)} of emails sent</p>
          <div className="meeting-cost"><span>Cost per held meeting</span><strong>{formatMoney(data.today.headline.cost_per_held_meeting)}</strong></div>
        </div>
      </section>

      <section aria-labelledby="snapshot-title">
        <SectionHeading id="snapshot-title" title="Operating snapshot" aside={<DataTrust data={data} />} />
        <div className="metric-grid">
          <Metric label="Interested replies" value={displayNumber(data.today.headline.positive_replies, data.today.headline.emails_sent > 0)} note={`${data.today.headline.total_replies} total replies`} icon={MessageSquareText} />
          <Metric label="Leads contacted" value={displayNumber(data.today.headline.leads_contacted, data.today.headline.emails_sent > 0)} note={`${formatNumber(data.today.headline.emails_sent)} emails sent`} icon={Users} />
          <Metric label="Cost per reply" value={formatMoney(data.today.headline.cost_per_reply)} note={`${formatMoney(data.today.headline.cost_usd)} total cost`} icon={CircleDollarSign} />
          <Metric label="Review queue" value={formatNumber(data.deliverability.health.review_queue)} note="Excluded from authoritative rates" icon={ClipboardCheck} />
        </div>
      </section>

      <section className="two-column-main" aria-label="Work and approvals">
        <div>
          <SectionHeading title="Work requiring attention" aside={<span className="section-count">{data.today.work_items.length} items</span>} />
          <div className="card list-card">
            {data.today.work_items.length ? data.today.work_items.map((item) => (
              <Link to={item.href} className="work-row" key={item.id}>
                <PriorityMark priority={item.priority} />
                <span className="work-copy"><small>{item.category}</small><strong>{item.title}</strong><span>{item.detail}</span></span>
                <span className="work-meta">{item.due_at ? relativeTime(item.due_at) : 'Review'}<ChevronRight size={16} /></span>
              </Link>
            )) : <EmptyState icon={BadgeCheck} title="Nothing urgent" body="The queue is clear. New replies, gates, and system alerts will appear here." />}
          </div>
        </div>
        <div>
          <SectionHeading title="Awaiting approval" aside={<Link to="/campaigns" className="text-link">View all</Link>} />
          <div className="approval-stack">
            {pending.length ? pending.slice(0, 3).map((approval) => <ApprovalCard key={approval.id} approval={approval} preview={preview} />) : (
              <div className="card"><EmptyState icon={ClipboardCheck} title="No pending gates" body="AI proposals and campaign gates will wait here for a human decision." /></div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function CampaignsPage({ data, preview }: { data: CockpitSnapshot; preview: boolean }) {
  const [creating, setCreating] = useState(false);
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Guided lifecycle"
        title="Campaigns"
        description="Every campaign follows the same evidence-backed path from intake to held meetings."
        action={<button className="primary-button" onClick={() => setCreating(true)}><Plus size={17} /> New campaign</button>}
      />
      <div className="method-strip" aria-label="Operating method">
        {['Prove it manually', 'Package the playbook', 'Run on demand', 'Schedule only when earned'].map((step, index) => (
          <div key={step}><span>{index + 1}</span><strong>{step}</strong>{index < 3 ? <ArrowRight size={15} /> : null}</div>
        ))}
      </div>
      <section>
        <SectionHeading title="Active work" aside={<span className="section-count">{data.campaigns.length} campaigns</span>} />
        <div className="campaign-grid">
          {data.campaigns.length ? data.campaigns.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} />) : (
            <div className="card span-full"><EmptyState icon={Target} title="Create the first campaign" body="Start with the sender interview. The cockpit will guide every decision after that." action={<button className="secondary-button" onClick={() => setCreating(true)}>Start intake</button>} /></div>
          )}
        </div>
      </section>
      <section className="education-card">
        <div><span className="eyebrow">Why this matters</span><h2>The system diagnoses before it optimizes.</h2></div>
        <p>Every weekly review checks deliverability first, then list quality, then copy and offer. That prevents a polished rewrite from hiding a damaged domain or a weak audience.</p>
      </section>
      {creating ? <CreateCampaignDialog preview={preview} onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CampaignCard({ campaign }: { campaign: Campaign }) {
  const progress = campaign.total_steps ? campaign.completed_steps / campaign.total_steps : 0;
  return (
    <Link to={`/campaigns/${campaign.id}`} className="campaign-card card">
      <div className="campaign-top"><Pill tone={campaign.status === 'active' ? 'good' : 'neutral'}>{campaign.status}</Pill><MoreHorizontal size={18} /></div>
      <h2>{campaign.label}</h2>
      <p>{campaign.niche ?? 'Niche not set'}</p>
      <div className="campaign-meta"><span>{campaign.platform ?? 'Platform pending'}</span><span>{campaign.source_method ?? 'Source pending'}</span></div>
      <div className="progress-copy"><span>{phaseLabels[campaign.current_phase] ?? humanize(campaign.current_phase)}</span><strong>{campaign.completed_steps}/{campaign.total_steps}</strong></div>
      <div className="progress-track"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      <div className="campaign-footer">
        <span>{campaign.pending_approvals ? `${campaign.pending_approvals} approval waiting` : 'No blocked gates'}</span>
        <span>Open <ArrowRight size={15} /></span>
      </div>
    </Link>
  );
}

function CampaignWorkspacePage({ data, preview }: { data: CockpitSnapshot; preview: boolean }) {
  const { campaignId = '' } = useParams();
  const campaign = data.campaigns.find((row) => row.id === campaignId);
  const workspace = useQuery({
    queryKey: ['campaign-workspace', campaignId, preview],
    queryFn: () => preview ? Promise.resolve(previewWorkspace(campaign)) : getCampaignWorkspace(campaignId),
    enabled: Boolean(campaignId),
  });
  if (!campaign) return <NotFound title="Campaign not found" to="/campaigns" />;
  if (workspace.isPending) return <PanelLoading />;
  if (workspace.isError) return <InlineError error={workspace.error} />;
  const current = String(workspace.data.campaign.current_phase ?? campaign.current_phase);
  const currentStep = workspace.data.steps.find((step) => step.phase_key === current);
  return (
    <div className="page-stack campaign-workspace">
      <PageHeader
        eyebrow={<Link to="/campaigns" className="back-link">Campaigns /</Link>}
        title={campaign.label}
        description={`${campaign.niche ?? 'Niche pending'} · ${campaign.platform ?? 'Platform pending'}`}
        action={<button className="secondary-button"><MoreHorizontal size={17} /> Campaign actions</button>}
      />
      <div className="phase-rail" aria-label="Campaign phases">
        {workspace.data.phases.map((phase, index) => {
          const step = workspace.data.steps.find((row) => row.phase_key === phase.key);
          const complete = step?.status === 'complete' || step?.status === 'approved';
          const active = phase.key === current;
          return (
            <button type="button" key={phase.key} className={`phase-step ${active ? 'active' : ''} ${complete ? 'complete' : ''}`}>
              <span>{complete ? <Check size={14} /> : index + 1}</span>
              <small>{phase.label}</small>
            </button>
          );
        })}
      </div>
      <div className="campaign-stage-layout">
        <section className="card stage-card">
          <div className="stage-heading"><div><span className="eyebrow">Current phase</span><h2>{phaseLabels[current] ?? humanize(current)}</h2></div>{currentStep?.gate_required ? <Pill tone="warning">Human gate</Pill> : <Pill tone="info">Guided work</Pill>}</div>
          <p className="stage-intro">{phaseGuidance(current)}</p>
          <div className="checklist">
            {(currentStep?.checklist ?? []).map((item, index) => (
              <label key={item} className="check-row"><input type="checkbox" defaultChecked={index === 0 && current !== 'intake'} /><span><strong>{item}</strong><small>{checklistHelp(current, index)}</small></span></label>
            ))}
          </div>
          <div className="stage-actions"><button className="primary-button">Continue phase <ArrowRight size={17} /></button><button className="ghost-button"><Sparkles size={16} /> Ask for help</button></div>
        </section>
        <aside className="stage-aside">
          <div className="card evidence-card"><div className="card-kicker"><ShieldCheck size={16} /> Evidence & assumptions</div><h3>No hidden AI leaps</h3><p>Every research claim, commonality, and quantitative promise must point to an approved source.</p><button className="text-button">Review evidence <ChevronRight size={15} /></button></div>
          <div className="card"><div className="card-kicker"><Activity size={16} /> Activity</div><div className="mini-timeline"><div><span /><p><strong>Workflow opened</strong><small>{formatDateTime(campaign.created_at)}</small></p></div><div><span /><p><strong>{campaign.completed_steps} phases complete</strong><small>All decisions recorded</small></p></div></div></div>
        </aside>
      </div>
    </div>
  );
}

function InboxPage({ data }: { data: CockpitSnapshot }) {
  const [selectedId, setSelectedId] = useState(data.inbox[0]?.id ?? null);
  const selected = data.inbox.find((reply) => reply.id === selectedId) ?? data.inbox[0];
  return (
    <div className="page-stack inbox-page">
      <PageHeader eyebrow="Reply operations" title="Inbox" description="Interested people first. Every response stays human-owned." action={<button className="secondary-button"><ListFilter size={17} /> Filters</button>} />
      <div className="inbox-layout card">
        <div className="reply-list" aria-label="Reply list">
          <div className="reply-list-head"><strong>{data.inbox.length} open replies</strong><span><span className="health-dot healthy" /> SLA live</span></div>
          {data.inbox.length ? data.inbox.map((reply) => <ReplyListItem key={reply.id} reply={reply} selected={reply.id === selected?.id} onSelect={() => setSelectedId(reply.id)} />) : <EmptyState icon={Inbox} title="Inbox clear" body="New replies will be classified and prioritized here." />}
        </div>
        <div className="reply-detail">
          {selected ? <ReplyDetail reply={selected} /> : <EmptyState icon={Mail} title="Choose a reply" body="The conversation and guided response actions will appear here." />}
        </div>
      </div>
    </div>
  );
}

function ReplyListItem({ reply, selected, onSelect }: { reply: InboxReply; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className={`reply-list-item ${selected ? 'selected' : ''}`} onClick={onSelect}>
      <span className="reply-avatar">{initials(reply.lead_name)}</span>
      <span className="reply-item-copy"><span><strong>{reply.lead_name}</strong><small>{relativeTime(reply.received_at)}</small></span><b>{reply.company ?? reply.campaign ?? 'Unknown company'}</b><p>{reply.preview}</p><span className="reply-tags"><Pill tone={toneFor(reply.classification)}>{humanize(reply.classification ?? 'unclassified')}</Pill>{reply.sla_due_at ? <small className="sla"><Clock3 size={12} /> {relativeTime(reply.sla_due_at)}</small> : null}</span></span>
    </button>
  );
}

function ReplyDetail({ reply }: { reply: InboxReply }) {
  const [drafting, setDrafting] = useState(false);
  return (
    <>
      <div className="reply-detail-head"><div><span className="reply-avatar large">{initials(reply.lead_name)}</span><span><h2>{reply.lead_name}</h2><p>{reply.company ?? 'Company unavailable'} · {reply.campaign ?? 'Campaign unavailable'}</p></span></div><button className="icon-button" aria-label="More reply actions"><MoreHorizontal size={19} /></button></div>
      <div className="conversation">
        <div className="message outgoing"><small>You · 2 days ago</small><p>Reaching out because your team’s expansion into three locations looked close to work we’ve already supported.</p></div>
        <div className="message incoming"><small>{reply.lead_name} · {relativeTime(reply.received_at)}</small><p>{reply.preview}</p></div>
      </div>
      <div className="reply-intelligence">
        <div><Bot size={17} /><span><strong>Outbound Brain read</strong><small>{humanize(reply.classification ?? 'unclassified')} · {reply.confidence ? `${Math.round(reply.confidence * 100)}% confidence` : 'Needs review'}</small></span></div>
        <button className="text-button">Why? <ChevronRight size={14} /></button>
      </div>
      {drafting ? (
        <div className="draft-box"><div className="draft-head"><span><Sparkles size={15} /> Suggested reply · Draft only</span><Pill tone="warning">Approval required</Pill></div><textarea aria-label="Reply draft" defaultValue={`Thanks ${firstName(reply.lead_name)} — happy to. I can walk you through the three-location setup and show exactly where the handoff stays human. Would Tuesday afternoon work?`} /><div className="draft-actions"><button className="primary-button">Request send approval</button><button className="secondary-button" onClick={() => navigator.clipboard?.writeText(reply.preview)}>Copy draft</button></div></div>
      ) : <button className="primary-button compose-button" onClick={() => setDrafting(true)}><Sparkles size={17} /> Draft two responses</button>}
      <div className="reply-footer-actions"><button className="ghost-button"><Clock3 size={16} /> Follow up later</button><button className="ghost-button"><Users size={16} /> Send to GHL</button><button className="ghost-button danger-text"><XCircle size={16} /> Suppress</button></div>
    </>
  );
}

function ExperimentsPage({ data }: { data: CockpitSnapshot }) {
  return (
    <div className="page-stack">
      <PageHeader eyebrow="Weekly experiment ritual" title="Experiments" description="One dimension at a time. Held meetings decide; reply rates only diagnose." action={<button className="primary-button"><Plus size={17} /> Pre-register test</button>} />
      <div className="principle-grid">
        <Principle icon={Target} title="One primary outcome" body="Qualified meeting held" />
        <Principle icon={ListFilter} title="One test dimension" body="Copy, source, or platform" />
        <Principle icon={Gauge} title="Power before verdict" body="500 judge · 1,000 crown floor" />
      </div>
      <section>
        <SectionHeading title="Experiment board" aside={<span className="section-count">{data.experiments.filter((row) => row.status === 'active').length} active</span>} />
        <div className="experiment-stack">
          {data.experiments.length ? data.experiments.map((experiment) => {
            const judgeProgress = Math.min(100, Math.round((experiment.sample_size / experiment.minimum_n) * 100));
            return (
              <div className="card experiment-card" key={experiment.id}>
                <div className="experiment-title"><div><Pill tone={toneFor(experiment.status)}>{experiment.status}</Pill><h2>{experiment.label}</h2><p>{experiment.campaign}</p></div><Pill tone={toneFor(experiment.recommendation)}>{experiment.recommendation?.toUpperCase() ?? 'NO VERDICT'}</Pill></div>
                <div className="hypothesis"><small>Pre-registered hypothesis</small><p>{experiment.hypothesis}</p></div>
                <div className="experiment-stats"><span><small>Test</small><strong>{humanize(experiment.test_dimension)}</strong></span><span><small>Primary outcome</small><strong>Meeting held</strong></span><span><small>MDE</small><strong>{formatPercent(experiment.mde)}</strong></span><span><small>Sample</small><strong>{formatNumber(experiment.sample_size)}</strong></span></div>
                <div className="power-row"><div><span>Judging floor</span><strong>{formatNumber(experiment.sample_size)} / {formatNumber(experiment.minimum_n)}</strong></div><div className="progress-track"><span style={{ width: `${judgeProgress}%` }} /></div><p>{experiment.rationale}</p></div>
                <div className="experiment-footer"><span><ShieldCheck size={15} /> No decision is automatic</span><button className="secondary-button">Open review</button></div>
              </div>
            );
          }) : <div className="card"><EmptyState icon={FlaskConical} title="No experiments yet" body="Pre-register the first single-dimension hypothesis before assigning leads." /></div>}
        </div>
      </section>
    </div>
  );
}

function LeadsPage({ data }: { data: CockpitSnapshot }) {
  return (
    <div className="page-stack">
      <PageHeader eyebrow="List lab" title="Leads" description="Sample before spend. Verify, deduplicate, and keep catch-alls separate." action={<button className="primary-button"><Plus size={17} /> New lead batch</button>} />
      <section className="quality-rule card">
        <div className="quality-rule-score"><span>15</span><small>of 20</small></div>
        <div><span className="eyebrow">Non-negotiable quality gate</span><h2>A batch must clear the ICP sample before enrichment.</h2><p>If more than five sampled records miss the ICP, revise the source filters and sample again. Tier-A lists under 500 stay manual.</p></div>
      </section>
      <section>
        <SectionHeading title="Lead batches" aside={<button className="filter-button"><ListFilter size={15} /> All sources</button>} />
        <div className="table-card card">
          <table>
            <thead><tr><th>Batch</th><th>Source</th><th>Sample gate</th><th>Records</th><th>Cost</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{data.leads.map((batch) => (
              <tr key={batch.id}><td><strong>{batch.campaign ?? 'Unassigned batch'}</strong><small>{humanize(batch.tier)}</small></td><td>{humanize(batch.source_method)}</td><td><span className="sample-score"><strong>{batch.sample_qualified}/{batch.sample_size || 20}</strong><span className={`mini-progress ${batch.sample_passed ? 'pass' : ''}`}><i style={{ width: `${Math.min(100, (batch.sample_qualified / 15) * 100)}%` }} /></span></span></td><td>{formatNumber(batch.total_leads)}</td><td>{formatMoney(batch.cost_usd)}</td><td><Pill tone={batch.sample_passed ? 'good' : batch.status === 'sampling' ? 'warning' : 'neutral'}>{batch.sample_passed ? 'Gate passed' : humanize(batch.status)}</Pill></td><td><button className="icon-button" aria-label={`Actions for ${batch.campaign}`}><MoreHorizontal size={17} /></button></td></tr>
            ))}</tbody>
          </table>
          {!data.leads.length ? <EmptyState icon={Users} title="No lead batches" body="Start with source filters, then review 20 representative records." /> : null}
        </div>
      </section>
    </div>
  );
}

function DeliverabilityPage({ data }: { data: CockpitSnapshot }) {
  const health = data.deliverability.health;
  const overall = health.deliverability.overall;
  return (
    <div className="page-stack">
      <PageHeader eyebrow="Sending safety" title="Deliverability" description="The first diagnostic layer. Sending stops before downstream metrics can distract you." action={<button className="secondary-button"><Activity size={17} /> Run checks</button>} />
      {health.warnings.length ? <div className="alert danger"><AlertTriangle size={20} /><span><strong>Attention required</strong>{health.warnings[0]}</span><button className="secondary-button">Review path</button></div> : <div className="alert success"><BadgeCheck size={20} /><span><strong>Sending paths are within policy</strong>No stop-sending threshold is active.</span></div>}
      <div className="metric-grid deliverability-metrics">
        <Metric label="Bounce rate" value={formatRate(overall.bounce)} note={`Pause at 5.00% · n=${overall.bounce.denominator}`} icon={Activity} tone={overall.bounce_flag === 'pause' ? 'danger' : 'neutral'} />
        <Metric label="Complaint rate" value={formatRate(overall.complaint)} note={`Pause at 0.30% · n=${overall.complaint.denominator}`} icon={AlertTriangle} tone={overall.complaint_flag === 'pause' ? 'danger' : 'neutral'} />
        <Metric label="Active suppressions" value={formatNumber(health.suppressions.total)} note="Applied before every follow-up" icon={ShieldCheck} />
        <Metric label="Review queue" value={formatNumber(health.review_queue)} note="Excluded from trusted reporting" icon={ClipboardCheck} />
      </div>
      <section>
        <SectionHeading title="Sending domains" aside={<span className="section-count">{data.deliverability.domains.length} domains</span>} />
        <div className="domain-grid">
          {data.deliverability.domains.map((domain) => (
            <div className="card domain-card" key={domain.id}><div className="domain-head"><span className={`domain-icon ${domain.status}`}><Mail size={18} /></span><Pill tone={toneFor(domain.status)}>{domain.status}</Pill></div><h2>{domain.domain}</h2><p>{domain.provider ?? 'Provider not set'}</p><dl><div><dt>Daily cap</dt><dd>{domain.daily_cap}</dd></div><div><dt>DNS failures</dt><dd>{domain.failing_checks}</dd></div><div><dt>Warm-up</dt><dd>{domain.warmup_started_at ? daysSince(domain.warmup_started_at) : 'Not started'}</dd></div></dl></div>
          ))}
          {!data.deliverability.domains.length ? <div className="card span-full"><EmptyState icon={Mail} title="No sending domains connected" body="Connect a domain, start warm-up, and verify authentication before campaign work continues." /></div> : null}
        </div>
      </section>
      <section>
        <SectionHeading title="Data feeds" />
        <div className="card feed-list">{health.feeds.length ? health.feeds.map((feed) => <div className="feed-row" key={`${feed.source}-${feed.scope_ref}`}><span className={`health-dot ${feed.health === 'ok' && (feed.stale_hours ?? Infinity) < 24 ? 'healthy' : 'warning'}`} /><span><strong>{humanize(feed.source)}</strong><small>{feed.scope_ref || 'Primary feed'}</small></span><span><strong>{feed.stale_hours === null ? 'Never synced' : `${feed.stale_hours}h ago`}</strong><small>{feed.last_error ?? `${feed.consecutive_failures} consecutive failures`}</small></span><Pill tone={toneFor(feed.health)}>{feed.health}</Pill></div>) : <EmptyState icon={Activity} title="No feeds have reported yet" body="Connect Instantly, EdgeMail, and GHL before trusting performance metrics." />}</div>
      </section>
    </div>
  );
}

function InsightsPage({ data }: { data: CockpitSnapshot }) {
  const [dimension, setDimension] = useState<'source' | 'platform' | 'campaign' | 'niche'>('source');
  const reports = { source: data.insights.by_source, platform: data.insights.by_platform, campaign: data.insights.by_campaign, niche: data.insights.by_niche };
  const report = reports[dimension];
  return (
    <div className="page-stack">
      <PageHeader eyebrow="Decision intelligence" title="Insights" description="Every rate carries its denominator. Every winner must earn statistical permission." action={<button className="secondary-button" onClick={() => downloadFunnel(report)}><Download size={17} /> Export CSV</button>} />
      <div className="question-grid">
        <QuestionCard number="01" question="Which source produces the best outcomes per dollar?" active={dimension === 'source'} onClick={() => setDimension('source')} />
        <QuestionCard number="02" question="Which platform performs better with copy held constant?" active={dimension === 'platform'} onClick={() => setDimension('platform')} />
        <QuestionCard number="03" question="Which objections and copy patterns matter by niche?" active={dimension === 'niche'} onClick={() => setDimension('niche')} />
      </div>
      {report.warnings.length ? <div className="alert warning"><AlertTriangle size={19} /><span><strong>Interpret with care</strong>{report.warnings[0]}</span></div> : null}
      <section className="card insight-card">
        <div className="insight-card-head"><div><span className="eyebrow">Grouped by {humanize(report.group_by)}</span><h2>Qualified meetings held</h2></div><div className="segmented-control" aria-label="Insight grouping">{(['source', 'platform', 'campaign', 'niche'] as const).map((key) => <button key={key} className={dimension === key ? 'active' : ''} onClick={() => setDimension(key)}>{humanize(key)}</button>)}</div></div>
        <FunnelBars report={report} />
      </section>
      <section className="two-column-main insight-bottom">
        <div><SectionHeading title="Experiment verdicts" /><div className="card verdict-list">{report.comparison.verdicts.map((verdict) => <div key={verdict.bucket}><Pill tone={toneFor(verdict.verdict)}>{verdict.verdict}</Pill><span><strong>{verdict.bucket}</strong><small>{verdict.reason}</small></span></div>)}</div></div>
        <div><SectionHeading title="Top objections" /><div className="card objection-list">{data.insights.objections.buckets[0]?.objections.length ? data.insights.objections.buckets[0].objections.map((objection) => <div key={objection.objection_type}><span><strong>{humanize(objection.objection_type)}</strong><small>“{objection.example ?? 'No grounded quote available'}”</small></span><b>{formatRate(objection.share)}</b></div>) : <EmptyState icon={MessageSquareText} title="No trusted objections yet" body="Classified, grounded reply evidence will appear here." />}</div></div>
      </section>
    </div>
  );
}

function FunnelBars({ report }: { report: FunnelReport }) {
  const max = Math.max(1, ...report.buckets.map((bucket) => bucket.counts.meeting_held));
  return (
    <div className="funnel-visual">
      <div className="bar-chart" aria-hidden="true">{report.buckets.map((bucket) => <div className="bar-row" key={bucket.bucket}><span>{bucket.bucket}</span><div><i style={{ width: `${Math.max(3, (bucket.counts.meeting_held / max) * 100)}%` }} /></div><strong>{bucket.counts.meeting_held}</strong></div>)}</div>
      <div className="table-card accessible-data-table"><table><caption>Held meeting performance grouped by {report.group_by}</caption><thead><tr><th>Group</th><th>Emails sent</th><th>Replies</th><th>Held meetings</th><th>Held rate</th><th>Power</th></tr></thead><tbody>{report.buckets.map((bucket) => <tr key={bucket.bucket}><td><strong>{bucket.bucket}</strong></td><td>{formatNumber(bucket.counts.emails_sent)}</td><td>{formatNumber(bucket.counts.reply)}</td><td>{formatNumber(bucket.counts.meeting_held)}</td><td>{formatRate(bucket.rates.meeting_held)}</td><td><Pill tone={toneFor(bucket.power)}>{humanize(bucket.power)}</Pill></td></tr>)}</tbody></table></div>
    </div>
  );
}

function PlaybooksPage({ data }: { data: CockpitSnapshot }) {
  return (
    <div className="page-stack">
      <PageHeader eyebrow="AI operations" title="Playbooks & routines" description="Automation earns autonomy one verified step at a time." action={<button className="primary-button"><Plus size={17} /> New test run</button>} />
      <div className="automation-ladder">
        <LadderStep number="01" icon={Search} title="Test run" body="5–10 records with full preview" active />
        <ArrowRight size={18} />
        <LadderStep number="02" icon={BookOpen} title="Playbook" body="Approved, reusable instructions" />
        <ArrowRight size={18} />
        <LadderStep number="03" icon={Play} title="On demand" body="Human starts every run" />
        <ArrowRight size={18} />
        <LadderStep number="04" icon={Clock3} title="Routine" body="Scheduled only when justified" />
      </div>
      <section>
        <SectionHeading title="Recent runs" aside={<span className="section-count">Live cost and progress</span>} />
        <div className="routine-grid">
          {data.playbooks.routines.map((job) => (
            <div className="card routine-card" key={job.id}><div className="routine-head"><span className={`routine-icon ${job.status}`}><Bot size={18} /></span><Pill tone={toneFor(job.status)}>{humanize(job.status)}</Pill></div><h2>{job.label}</h2><p>{humanize(job.kind)} · {humanize(job.mode)}</p><div className="routine-progress"><div><span>Records</span><strong>{job.processed}/{job.total}</strong></div><div className="progress-track"><span style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }} /></div></div><dl><div><dt>Cost</dt><dd>{formatMoney(job.actual_cost_usd)}</dd></div><div><dt>Retries</dt><dd>{job.retry_count}/3</dd></div></dl>{job.error_message ? <div className="routine-error"><AlertTriangle size={14} /> {job.error_message}</div> : null}<div className="routine-actions"><button className="ghost-button">View run</button>{job.status === 'failed' ? <button className="secondary-button">Review repair</button> : null}</div></div>
          ))}
          {!data.playbooks.routines.length ? <div className="card span-full"><EmptyState icon={Bot} title="No playbook runs yet" body="Start with a small test run and inspect every output before promotion." /></div> : null}
        </div>
      </section>
      <section className="two-column-main">
        <div><SectionHeading title="Sender knowledge" /><div className="card profile-list">{data.playbooks.sender_profiles.length ? data.playbooks.sender_profiles.map((profile) => <div key={profile.id}><span className="user-avatar">{initials(profile.name)}</span><span><strong>{profile.name}</strong><small>Version {profile.version} · {profile.approved_facts} proven facts · {profile.approved_claims} claims</small></span><button className="text-button">Open <ChevronRight size={14} /></button></div>) : <EmptyState icon={Users} title="No sender profile" body="Complete the sender interview before AI can suggest genuine commonality." />}</div></div>
        <div><SectionHeading title="Policy packs" /><div className="card policy-list">{data.playbooks.policies.length ? data.playbooks.policies.map((policy) => <div key={policy.id}><span><strong>{policy.label}</strong><small>{policy.jurisdiction} · {policy.provider ?? 'All providers'} · {humanize(policy.volume_class)}</small></span><Pill tone={toneFor(policy.status)}>{policy.status}</Pill></div>) : <EmptyState icon={ShieldCheck} title="No active policy" body="A jurisdiction and provider policy is required before preflight can pass." />}</div></div>
      </section>
    </div>
  );
}

function SettingsPage({ data }: { data: CockpitSnapshot }) {
  return (
    <div className="page-stack">
      <PageHeader eyebrow="Workspace controls" title="Settings" description="People, integrations, policies, and the audit record." />
      <section className="settings-section"><div className="settings-title"><Users size={19} /><span><h2>Members & roles</h2><p>Access stays tenant-scoped and server-enforced.</p></span><button className="secondary-button"><Plus size={16} /> Invite member</button></div><div className="card member-list">{data.members.length ? data.members.map((member) => <div key={member.id}><span className="user-avatar">{initials(member.display_name ?? member.email)}</span><span><strong>{member.display_name ?? member.email}</strong><small>{member.email}</small></span><Pill tone={member.status === 'active' ? 'good' : 'neutral'}>{member.role}</Pill></div>) : <EmptyState icon={Users} title="No stored members" body="The bootstrap administrator can add the first operator here." />}</div></section>
      <section className="settings-section"><div className="settings-title"><Activity size={19} /><span><h2>Integrations</h2><p>Credentials stay in encrypted platform secrets, never in D1 or the browser.</p></span></div><div className="integration-grid">{['edgemail', 'instantly', 'ghl'].map((source) => { const feed = data.deliverability.health.feeds.find((row) => row.source === source); return <div className="card integration-card" key={source}><span className="integration-logo">{source.slice(0, 1).toUpperCase()}</span><span><strong>{humanize(source)}</strong><small>{feed ? `Last sync ${feed.stale_hours ?? 'unknown'}h ago` : 'Not connected'}</small></span><Pill tone={feed?.health === 'ok' ? 'good' : feed ? 'warning' : 'neutral'}>{feed?.health ?? 'Connect'}</Pill></div>; })}</div></section>
      <section className="settings-section"><div className="settings-title"><ClipboardCheck size={19} /><span><h2>Audit history</h2><p>Every consequential decision, actor, reason, and version.</p></span><button className="secondary-button"><Download size={16} /> Export</button></div><div className="card audit-list">{data.audit.length ? data.audit.map((event) => <div key={event.id}><span className="audit-icon"><Activity size={15} /></span><span><strong>{event.actor_email} {event.action} {humanize(event.entity_type)}</strong><small>{event.reason ?? `Record ${event.entity_id}`} · {formatDateTime(event.occurred_at)}</small></span><Pill tone="neutral">{event.actor_role}</Pill></div>) : <EmptyState icon={ClipboardCheck} title="No audit events yet" body="Approved changes and operator actions will be recorded here." />}</div></section>
    </div>
  );
}

function ApprovalCard({ approval, preview }: { approval: Approval; preview: boolean }) {
  const [deciding, setDeciding] = useState(false);
  return <><button className="card approval-card" onClick={() => setDeciding(true)}><div className="approval-card-head"><Pill tone={toneFor(approval.risk)}>{approval.risk} risk</Pill><small>{relativeTime(approval.requested_at)}</small></div><h3>{approval.title}</h3><p>{approval.summary}</p><div className="approval-meta"><span><Users size={14} /> {formatNumber(approval.affected_records)} records</span><span><CircleDollarSign size={14} /> {formatMoney(approval.estimated_cost_usd)}</span></div><span className="approval-open">Review decision <ArrowRight size={15} /></span></button>{deciding ? <ApprovalDialog approval={approval} preview={preview} onClose={() => setDeciding(false)} /> : null}</>;
}

function ApprovalDialog({ approval, preview, onClose }: { approval: Approval; preview: boolean; onClose: () => void }) {
  const [reason, setReason] = useState('Reviewed the evidence and affected records.');
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => preview ? Promise.resolve({ id: approval.id, status: decision, version: approval.version + 1 }) : decideApproval(approval.id, { decision, expected_version: approval.version, reason }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['cockpit'] }); onClose(); },
  });
  const diff = isRecord(approval.diff) ? approval.diff : {};
  return (
    <Modal title="Review proposed action" onClose={onClose} wide>
      <div className="decision-heading"><div><Pill tone={toneFor(approval.risk)}>{approval.risk} risk</Pill><h2>{approval.title}</h2><p>{approval.summary}</p></div><div className="decision-impact"><span><small>Records</small><strong>{formatNumber(approval.affected_records)}</strong></span><span><small>Estimated cost</small><strong>{formatMoney(approval.estimated_cost_usd)}</strong></span></div></div>
      <div className="decision-grid"><div><h3>Proposed change</h3><div className="diff-box">{Object.keys(diff).length ? Object.entries(diff).map(([key, value]) => <div key={key}><small>{humanize(key)}</small><strong>{Array.isArray(value) ? value.join(', ') : String(value)}</strong></div>) : <p>No structured diff was attached.</p>}</div></div><div><h3>Evidence</h3><ul className="evidence-list">{approval.evidence.length ? approval.evidence.map((item, index) => <li key={index}><BadgeCheck size={15} /> {typeof item === 'string' ? item : JSON.stringify(item)}</li>) : <li><AlertTriangle size={15} /> Evidence is missing; reject and request a grounded proposal.</li>}</ul></div></div>
      <label className="field"><span>Decision reason</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} /></label>
      {mutation.isError ? <InlineError error={mutation.error} /> : null}
      <div className="modal-actions"><button className="secondary-button danger-text" disabled={mutation.isPending || reason.trim().length < 3} onClick={() => mutation.mutate('rejected')}><XCircle size={16} /> Reject</button><button className="primary-button" disabled={mutation.isPending || reason.trim().length < 3} onClick={() => mutation.mutate('approved')}>{mutation.isPending ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />} Approve</button></div>
    </Modal>
  );
}

function CreateCampaignDialog({ preview, onClose }: { preview: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ label: '', platform: 'edgemail' as 'edgemail' | 'instantly' | 'manual', source_method: '', reason: 'Starting a new evidence-backed outbound campaign.' });
  const mutation = useMutation({
    mutationFn: () => preview ? Promise.resolve({ id: 'c1' }) : createCampaign({ ...form, source_method: form.source_method || null }),
    onSuccess: (result) => { void queryClient.invalidateQueries({ queryKey: ['cockpit'] }); onClose(); navigate(`/campaigns/${result.id}${preview && import.meta.env.DEV ? '?preview=1' : ''}`); },
  });
  const submit = (event: FormEvent) => { event.preventDefault(); mutation.mutate(); };
  return (
    <Modal title="Start a campaign" onClose={onClose}>
      <form onSubmit={submit} className="form-stack"><div className="dialog-intro"><span className="dialog-icon"><Target size={20} /></span><div><h2>Begin with intake</h2><p>No campaign can launch from this form. It creates the guided workspace and its human gates.</p></div></div><label className="field"><span>Campaign name</span><input autoFocus required minLength={3} maxLength={120} value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} placeholder="Roofing owners · warm proof" /></label><div className="form-grid"><label className="field"><span>Sending platform</span><select value={form.platform} onChange={(event) => setForm({ ...form, platform: event.target.value as typeof form.platform })}><option value="edgemail">EdgeMail</option><option value="instantly">Instantly</option><option value="manual">Decide later</option></select></label><label className="field"><span>Lead source</span><input value={form.source_method} onChange={(event) => setForm({ ...form, source_method: event.target.value })} placeholder="apify-maps" /></label></div><label className="field"><span>Why this campaign now?</span><textarea required minLength={3} rows={3} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} /></label>{mutation.isError ? <InlineError error={mutation.error} /> : null}<div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="submit" className="primary-button" disabled={mutation.isPending || form.label.trim().length < 3}>{mutation.isPending ? <LoaderCircle className="spin" size={16} /> : <ArrowRight size={16} />} Create intake</button></div></form>
    </Modal>
  );
}

function CommandPalette({ data, onClose }: { data: CockpitSnapshot; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const actions = [
    { label: 'Explain the current bottleneck', detail: data.today.bottleneck.title, icon: Gauge, path: '/today' },
    { label: 'Draft two replies', detail: `${data.inbox.length} replies in queue`, icon: MessageSquareText, path: '/inbox' },
    { label: 'Review a lead batch', detail: 'Check the 15-of-20 quality gate', icon: Users, path: '/leads' },
    { label: 'Check sending safety', detail: `${data.deliverability.health.warnings.length} active warnings`, icon: ShieldCheck, path: '/deliverability' },
    { label: 'Interpret an experiment', detail: 'Power, intervals, and next decision', icon: FlaskConical, path: '/experiments' },
    { label: 'Prepare a campaign', detail: 'Start with intake, not auto-launch', icon: Target, path: '/campaigns' },
  ].filter((item) => `${item.label} ${item.detail}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="modal-backdrop command-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="command-palette" role="dialog" aria-modal="true" aria-label="Ask Outbound Brain">
        <div className="command-input"><Sparkles size={18} /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ask a question or choose an action…" aria-label="Search Outbound Brain actions" /><kbd>Esc</kbd></div>
        <div className="command-context"><Bot size={15} /><span>AI can analyze and prepare. You approve anything consequential.</span></div>
        <div className="command-results">{actions.map(({ label, detail, icon: Icon, path }) => <button key={label} onClick={() => { navigate(path); onClose(); }}><span className="command-icon"><Icon size={17} /></span><span><strong>{label}</strong><small>{detail}</small></span><ArrowRight size={16} /></button>)}</div>
        <div className="command-footer"><span><Command size={13} /> Context: {data.session.workspace.name}</span><span>Evidence shown before action</span></div>
      </div>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, action }: { eyebrow: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return <header className="page-header"><div><div className="eyebrow">{eyebrow}</div><h1>{title}</h1>{description ? <p>{description}</p> : null}</div>{action ? <div className="page-actions">{action}</div> : null}</header>;
}

function SectionHeading({ id, title, aside }: { id?: string; title: string; aside?: ReactNode }) {
  return <div className="section-heading"><h2 id={id}>{title}</h2>{aside}</div>;
}

function Metric({ label, value, note, icon: Icon, tone = 'neutral' }: { label: string; value: string; note: string; icon: LucideIcon; tone?: StatusTone }) {
  return <div className={`metric-card card metric-${tone}`}><div><span className="metric-icon"><Icon size={17} /></span><small>{label}</small></div><strong>{value}</strong><p>{note}</p></div>;
}

function Pill({ children, tone = 'neutral' }: { children: ReactNode; tone?: StatusTone }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

function PriorityMark({ priority }: { priority: string }) {
  const icon = priority === 'critical' ? <Pause size={15} /> : priority === 'urgent' ? <Clock3 size={15} /> : priority === 'high' ? <AlertTriangle size={15} /> : <Activity size={15} />;
  return <span className={`priority-mark priority-${priority}`} aria-label={`${priority} priority`}>{icon}</span>;
}

function EmptyState({ icon: Icon, title, body, action }: { icon: LucideIcon; title: string; body: string; action?: ReactNode }) {
  return <div className="empty-state"><span><Icon size={21} /></span><h3>{title}</h3><p>{body}</p>{action}</div>;
}

function DataTrust({ data }: { data: CockpitSnapshot }) {
  const good = data.today.data_quality.freshness === 'current' && data.today.data_quality.completeness === 'good';
  return <span className={`data-trust ${good ? 'good' : 'warning'}`}><span className={`health-dot ${good ? 'healthy' : 'warning'}`} /> {good ? 'Data current and attributed' : 'Data needs attention'}</span>;
}

function Principle({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return <div className="principle-card card"><span><Icon size={18} /></span><div><strong>{title}</strong><p>{body}</p></div></div>;
}

function QuestionCard({ number, question, active, onClick }: { number: string; question: string; active: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} className={`question-card ${active ? 'active' : ''}`}><span>{number}</span><strong>{question}</strong><ArrowRight size={16} /></button>;
}

function LadderStep({ number, icon: Icon, title, body, active = false }: { number: string; icon: LucideIcon; title: string; body: string; active?: boolean }) {
  return <div className={`ladder-step ${active ? 'active' : ''}`}><div><span>{number}</span><Icon size={18} /></div><strong>{title}</strong><p>{body}</p></div>;
}

function Modal({ title, children, onClose, wide = false }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}><div className="modal-head"><span>{title}</span><button className="icon-button" onClick={onClose} aria-label="Close dialog"><X size={19} /></button></div><div className="modal-body">{children}</div></div></div>;
}

function LoadingScreen() {
  return <div className="loading-screen" role="status"><span className="brand-mark"><Zap size={20} /></span><LoaderCircle className="spin" size={22} /><strong>Preparing your operator view</strong><p>Checking replies, gates, experiments, and system health.</p></div>;
}

function PanelLoading() {
  return <div className="card panel-loading" role="status"><LoaderCircle className="spin" size={20} /> Loading campaign workspace…</div>;
}

function ConnectionScreen({ error }: { error: Error }) {
  const apiError = error instanceof ApiError ? error : null;
  return <div className="connection-screen"><span className="connection-icon"><ShieldCheck size={26} /></span><span className="eyebrow">Secure operator cockpit</span><h1>{apiError?.status === 401 || apiError?.status === 403 ? 'Sign-in or workspace access is required.' : 'The cockpit could not load.'}</h1><p>{error.message}</p>{apiError?.remediation ? <small>{apiError.remediation}</small> : null}<div><button className="primary-button" onClick={() => window.location.reload()}>Try again</button>{import.meta.env.DEV ? <a className="secondary-button" href="?preview=1">Open local preview</a> : null}</div></div>;
}

function InlineError({ error }: { error: Error }) {
  return <div className="inline-error" role="alert"><AlertTriangle size={16} /><span>{error.message}</span></div>;
}

function NotFound({ title, to }: { title: string; to: string }) {
  return <div className="card"><EmptyState icon={Search} title={title} body="This record may have moved or belongs to another workspace." action={<Link className="secondary-button" to={to}>Go back</Link>} /></div>;
}

function previewWorkspace(campaign?: Campaign) {
  const phases = Object.entries(phaseLabels).map(([key, label], index) => ({ key, label, gate: [1, 2, 4, 5].includes(index) }));
  return {
    campaign: { id: campaign?.id ?? 'c1', label: campaign?.label ?? 'Campaign', current_phase: campaign?.current_phase ?? 'intake' },
    phases,
    steps: phases.map((phase, index) => ({ id: `step-${index}`, phase_key: phase.key, status: index < (campaign?.completed_steps ?? 0) ? 'complete' : phase.key === campaign?.current_phase ? 'active' : 'pending', gate_required: phase.gate, checklist: previewChecklist(phase.key), evidence: [] })),
    offers: [], lead_batches: [], copy_variants: [], approvals: [], experiments: [],
  };
}

function previewChecklist(phase: string): string[] {
  const values: Record<string, string[]> = {
    intake: ['Complete sender interview', 'Document credibility and proof', 'Approve Commonality Inventory'],
    research: ['Write the one-sentence goal', 'Approve evidence-backed ICP', 'Estimate TAM and start warm-up'],
    offer: ['Compare 3–5 offer archetypes', 'Score ROI, trust, and friction', 'Approve risk reversal'],
    list: ['Document source filters', 'Review 20 representative leads', 'Verify, deduplicate, and suppress'],
    copy: ['Create two different hypotheses', 'Validate approved claims', 'Review five rendered messages'],
    preflight: ['Check authentication and placement', 'Set caps and schedule', 'Activate compliance policy'],
    experiment: ['Review sample size and power', 'Classify replies and objections', 'Approve HOLD / CONTINUE / KILL / BREED'],
    reply_ops: ['Assign SLA owner', 'Activate scenario playbooks', 'Verify GHL handoff'],
  };
  return values[phase] ?? [];
}

function phaseGuidance(phase: string): string {
  const guidance: Record<string, string> = {
    intake: 'Capture the real sender before asking AI to speak on their behalf. Commonality without proof is not personalization.',
    research: 'Define one narrow audience, one measurable goal, and the evidence that makes the campaign worth running.',
    offer: 'Compare genuinely different ways to create value. The system scores options; a human chooses the promise.',
    list: 'Prove source quality on 20 records before spending money on the rest of the batch.',
    copy: 'Keep the human-written structure. AI may fill short, grounded slots and must show its sources.',
    preflight: 'No campaign launches until identity, placement, caps, tracking, and the active compliance policy pass.',
    experiment: 'Read held meetings first, then use reply and objection data to diagnose what changed.',
    reply_ops: 'Interested replies move first. The system prepares context; the operator owns the relationship.',
  };
  return guidance[phase] ?? 'Complete the evidence-backed checklist before advancing.';
}

function checklistHelp(phase: string, index: number): string {
  const fallback = ['Evidence required', 'Human review required', 'Recorded in audit history'];
  return phase === 'list' && index === 1 ? 'At least 15 of 20 must qualify' : fallback[index % fallback.length] ?? 'Evidence required';
}

function toneFor(status: string | null | undefined): StatusTone {
  if (!status) return 'neutral';
  if (['active', 'approved', 'complete', 'ready', 'healthy', 'ok', 'pass', 'leader', 'crownable', 'interested'].includes(status)) return 'good';
  if (['paused', 'failed', 'dead_letter', 'critical', 'danger', 'kill', 'rejected'].includes(status)) return 'danger';
  if (['warning', 'warn', 'pending', 'sampling', 'warming', 'high', 'medium', 'hold', 'underpowered', 'degraded'].includes(status)) return 'warning';
  if (['continue', 'running', 'drafting', 'info', 'judgeable'].includes(status)) return 'info';
  return 'neutral';
}

function formatRate(value: Rate): string {
  return value.rate === null ? 'Not measured' : formatPercent(value.rate);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: value < 0.01 ? 2 : 1, maximumFractionDigits: 2 }).format(value);
}

function formatMoney(value: number | null): string {
  return value === null ? 'Not measured' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: value >= 100 ? 0 : 2 }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function displayNumber(value: number, measured: boolean): string {
  return measured ? formatNumber(value) : 'Not measured';
}

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date);
}

function relativeTime(value: string): string {
  const delta = new Date(value).getTime() - Date.now();
  if (Number.isNaN(delta)) return 'Unknown';
  const minutes = Math.round(delta / 60_000);
  if (Math.abs(minutes) < 60) return minutes >= 0 ? `in ${Math.max(1, minutes)}m` : `${Math.abs(minutes)}m ago`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return hours >= 0 ? `in ${hours}h` : `${Math.abs(hours)}h ago`;
  const days = Math.round(hours / 24);
  return days >= 0 ? `in ${days}d` : `${Math.abs(days)}d ago`;
}

function daysSince(value: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000));
  return `${days} days`;
}

function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function initials(value: string): string {
  return value.split(/[\s@]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?';
}

function firstName(value: string): string {
  return value.trim().split(/\s+/)[0] || value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function downloadFunnel(report: FunnelReport) {
  const rows = [['group', 'emails_sent', 'replies', 'held_meetings', 'held_rate', 'power'], ...report.buckets.map((bucket: FunnelBucket) => [bucket.bucket, bucket.counts.emails_sent, bucket.counts.reply, bucket.counts.meeting_held, bucket.rates.meeting_held.rate ?? '', bucket.power])];
  const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `outbound-brain-${report.group_by}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
